<script setup>
/**
 * The whole connection, as seven sections in the order somebody actually builds one:
 * what it is → what it calls → what it fills in → what it sends → how it signs in →
 * what to keep → whether it is on.
 *
 * The anatomy is `TaskForm`'s, line for line — `SettingsHeader` with a one-sentence
 * description per section, `h-px bg-n-weak` rules between them, `gap-6` inside a section
 * against `gap-8` between them. This is the most technical screen in the product and it is
 * still the same screen: two pages in one section laid out differently teach an owner that
 * the product is two products.
 *
 * THREE THINGS THAT ARE SPECIFIC TO THIS FORM:
 *
 *  • **The short name exists once.** On a new connection it is a box; on a saved one it is
 *    a sentence saying what the tasks call it and that it cannot change. That is not a
 *    disabled input — a greyed-out box invites somebody to work out how to enable it, and
 *    there is nothing to enable: a rename would detach every task attached to it
 *    (`routes/api/tools.ts` decision 1).
 *
 *  • **"What it sends" appears only for the calls that send something.** A body on a GET is
 *    a section that cannot do anything, and a form that shows one is a form that gets it
 *    filled in.
 *
 *  • **The secret box is empty on a saved connection, always.** Nothing can read it back —
 *    not this page, not the BFF, not the sidecar — so the hint under it does the work an
 *    empty box otherwise does not: "a sign-in is already saved, leave this empty to keep
 *    it". Without that sentence an empty box reads as "no sign-in", which is the one
 *    reading that makes somebody clear a working credential.
 *
 * THIS COMPONENT OWNS NO STATE. Every edit is emitted as a whole replacement draft, which
 * keeps `vue/no-mutating-props` honest and keeps `connectionPatch` able to compute one
 * clean diff.
 */
import { computed, ref } from 'vue';

import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

import ConnectionParamList from './ConnectionParamList.vue';
import WasloExplainer from './WasloExplainer.vue';
import WasloInput from './WasloInput.vue';
import WasloSelect from './WasloSelect.vue';
import WasloTextArea from './WasloTextArea.vue';
import { useWasloI18n } from '../i18n.js';
import {
  CONNECTION_AUTH_TYPES,
  CONNECTION_METHODS,
  CONNECTION_PURPOSES,
  METHODS_WITH_BODY,
  TOKEN_EXAMPLE,
  looksUpByTypedIdentity,
  methodIsDefaultFor,
  signInNeedsReentry,
  withPurpose,
} from '../helpers/connection-draft.js';

const props = defineProps({
  /** The draft, in the shape `helpers/connection-draft.js` builds. */
  modelValue: { type: Object, required: true },
  /**
   * Keyed by section: name, description, purpose, url, params, body, auth, secret,
   * keepFields and enabled. Every key the BFF can pin a refusal on has a place on this
   * form; a message on a key with nowhere to show is a "try again" with no reason.
   */
  errors: { type: Object, default: () => ({}) },
  isNew: { type: Boolean, default: false },
  /** True when a sign-in is already stored — the only thing we know about the secret. */
  signInSaved: { type: Boolean, default: false },
  /** The stored short name, for the sentence that replaces the box after creation. */
  savedName: { type: String, default: '' },
  /**
   * The address as it is SAVED, which is the only thing "has this moved to a different
   * website" can be asked against. Read-only here; the draft's own `url` is the other half.
   */
  savedUrl: { type: String, default: '' },
  /**
   * Whether the switch may be turned ON (P-3.4): false for a new connection and for one
   * whose last test did not work. Turning it OFF is never gated.
   */
  canSwitchOn: { type: Boolean, default: true },
  /** `''`, `'untested'`, `'not_tried'` or `'changed'`: the sentence under the switch. */
  testState: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useWasloI18n();

const key = name => `WASLO.SETTINGS.CONNECTIONS.EDITOR.${name}`;

const patch = changes =>
  emit('update:modelValue', { ...props.modelValue, ...changes });

const showsBody = computed(() =>
  METHODS_WITH_BODY.includes(props.modelValue.method)
);

/**
 * The JSON example, built here rather than in `i18n.js`. A brace is vue-i18n's own
 * interpolation syntax, so a JSON sample in a message file is either escaped into
 * unreadability or silently mangled.
 */
const bodyPlaceholder = computed(() =>
  ['{', `  "area": "${TOKEN_EXAMPLE}"`, '}'].join('\n')
);

/**
 * What an EMPTY secret box means right now — and it means three different things.
 *
 * The third one used to be missing, and its absence was a small lie told at exactly the
 * wrong moment. `SECRET_HINT_HOST` was written for it, in both languages, and then
 * referenced by nothing: change the address to a different website and the box went on
 * saying "a sign-in is already saved, leave this empty to keep it" right up until the
 * save was refused. Both the BFF and the sidecar refuse that edit — a stored credential
 * is never sent to a website nobody has agreed to — so the box now says so before she
 * presses anything, and `validateDraft` refuses it with the same sentence.
 */
const secretHint = computed(() => {
  if (props.isNew || !props.signInSaved)
    return t(`${key('SIGN_IN')}.SECRET_HINT_NEW`);
  if (
    signInNeedsReentry(props.modelValue, {
      signInSaved: props.signInSaved,
      savedUrl: props.savedUrl,
    })
  ) {
    return t(`${key('SIGN_IN')}.SECRET_HINT_HOST`);
  }
  return t(`${key('SIGN_IN')}.SECRET_HINT_SAVED`);
});

const methodOptions = computed(() =>
  CONNECTION_METHODS.map(method => ({ value: method, label: method }))
);

const authOptions = computed(() =>
  CONNECTION_AUTH_TYPES.map(type => ({
    value: type,
    label: t(`${key('SIGN_IN')}.TYPES.${type.toUpperCase()}`),
  }))
);

const purposeOptions = computed(() =>
  CONNECTION_PURPOSES.map(value => ({
    value,
    label: t(`${key('PURPOSE')}.${value.toUpperCase()}`),
    hint: t(`${key('PURPOSE')}.${value.toUpperCase()}_HINT`),
  }))
);

const choosePurpose = value =>
  emit('update:modelValue', withPurpose(props.modelValue, value));

/**
 * Advanced is OPEN when the method is not the one its answer brings, so a developer's PUT is
 * visible at once and nobody else ever sees a verb. Derived rather than seeded once, because
 * the saved row can arrive after this form mounts; once the owner opens or shuts it herself,
 * her choice wins.
 */
const advancedToggled = ref(null);
const advancedOpen = computed(
  () =>
    advancedToggled.value ??
    ((props.modelValue.purpose ?? '') !== '' &&
      !methodIsDefaultFor(props.modelValue.purpose, props.modelValue.method))
);

const identityNote = computed(() => looksUpByTypedIdentity(props.modelValue));

const testHint = computed(() => {
  // A refusal on the switch says it in red; the same advice in amber above it is noise.
  if (props.errors.enabled) return '';
  if (props.testState === 'untested') return t(`${key('STATE')}.UNTESTED`);
  if (props.testState === 'not_tried') return t(`${key('STATE')}.NOT_TRIED`);
  if (props.testState === 'changed') return t(`${key('STATE')}.CHANGED`);
  return '';
});

const stateSentence = computed(() => {
  if (!props.modelValue.enabled) return t(`${key('STATE')}.OFF`);
  return props.modelValue.purpose === 'change'
    ? t(`${key('STATE')}.ON_CHANGE`)
    : t(`${key('STATE')}.ON`);
});
</script>

<template>
  <div class="flex flex-col gap-8 pb-8" data-waslo-connection-form>
    <WasloExplainer icon="i-lucide-plug" :note="t(key('SPOTLIGHT'))" />

    <!-- 1 · WHAT IT IS. -->
    <div class="flex flex-col gap-6">
      <SettingsHeader
        :heading="t(`${key('ABOUT')}.HEADING`)"
        :description="t(`${key('ABOUT')}.DESCRIPTION`)"
      />
      <WasloInput
        v-if="isNew"
        :model-value="modelValue.name"
        :label="t(`${key('NAME')}.LABEL`)"
        :placeholder="t(`${key('NAME')}.PLACEHOLDER`)"
        :hint="t(`${key('NAME')}.HINT`)"
        :message="errors.name || ''"
        :message-type="errors.name ? 'error' : 'info'"
        data-waslo-connection-name
        @update:model-value="value => patch({ name: value })"
      />
      <p
        v-else
        class="mb-0 text-sm text-n-slate-11"
        data-waslo-connection-name-fixed
      >
        {{ t(`${key('NAME')}.FIXED`, { name: savedName }) }}
      </p>
      <WasloTextArea
        :model-value="modelValue.description"
        :label="t(`${key('WHAT')}.LABEL`)"
        :placeholder="t(`${key('WHAT')}.PLACEHOLDER`)"
        :hint="t(`${key('WHAT')}.HINT`)"
        :message="errors.description || ''"
        message-type="error"
        :max-length="1000"
        min-height="3.5rem"
        data-waslo-connection-what
        @update:model-value="value => patch({ description: value })"
      />
    </div>

    <span class="w-full h-px mt-2 bg-n-weak" />

    <!-- 2 · WHAT IT CALLS. -->
    <div class="flex flex-col gap-6">
      <SettingsHeader
        :heading="t(`${key('CALL')}.HEADING`)"
        :description="t(`${key('CALL')}.DESCRIPTION`)"
      />
      <div
        class="flex flex-col gap-3"
        role="radiogroup"
        :aria-label="t(`${key('PURPOSE')}.LABEL`)"
        data-waslo-connection-purpose
      >
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t(`${key('PURPOSE')}.LABEL`) }}
        </p>
        <div class="grid gap-3 sm:grid-cols-2">
          <button
            v-for="option in purposeOptions"
            :key="option.value"
            type="button"
            role="radio"
            :aria-checked="
              modelValue.purpose === option.value ? 'true' : 'false'
            "
            class="flex flex-col items-start gap-1 p-4 outline outline-1 -outline-offset-1 rounded-xl text-start"
            :class="
              modelValue.purpose === option.value
                ? 'outline-n-blue-10 bg-n-blue-3'
                : 'outline-n-weak bg-n-solid-1 hover:bg-n-alpha-1'
            "
            :data-waslo-connection-purpose-option="option.value"
            @click="choosePurpose(option.value)"
          >
            <span class="text-sm font-medium text-n-slate-12">{{
              option.label
            }}</span>
            <span class="text-sm text-n-slate-11">{{ option.hint }}</span>
          </button>
        </div>
        <p
          v-if="errors.purpose"
          class="mb-0 text-sm text-n-ruby-9"
          data-waslo-connection-purpose-error
        >
          {{ errors.purpose }}
        </p>
      </div>
      <WasloInput
        :model-value="modelValue.url"
        :label="t(`${key('URL')}.LABEL`)"
        :placeholder="t(`${key('URL')}.PLACEHOLDER`)"
        :hint="t(`${key('URL')}.HINT`, { token: TOKEN_EXAMPLE })"
        :message="errors.url || ''"
        :message-type="errors.url ? 'error' : 'info'"
        data-waslo-connection-url
        @update:model-value="value => patch({ url: value })"
      />
      <details
        :open="advancedOpen"
        class="flex flex-col gap-3"
        data-waslo-connection-advanced
        @toggle="event => (advancedToggled = event.target.open)"
      >
        <summary class="text-sm cursor-pointer text-n-slate-11">
          {{ t(key('ADVANCED')) }}
        </summary>
        <WasloSelect
          class="mt-3 sm:w-44"
          :model-value="modelValue.method"
          :label="t(`${key('METHOD')}.LABEL`)"
          :options="methodOptions"
          data-waslo-connection-method
          @update:model-value="value => patch({ method: value })"
        />
      </details>
    </div>

    <span class="w-full h-px mt-2 bg-n-weak" />

    <!-- 3 · WHAT IT FILLS IN. -->
    <div class="flex flex-col gap-6">
      <SettingsHeader
        :heading="t(`${key('DETAILS')}.HEADING`)"
        :description="
          t(`${key('DETAILS')}.DESCRIPTION`, { token: TOKEN_EXAMPLE })
        "
      />
      <ConnectionParamList
        :model-value="modelValue.params"
        :message="errors.params || ''"
        @update:model-value="value => patch({ params: value })"
      />
      <p
        v-if="identityNote"
        class="mb-0 text-sm text-n-amber-11"
        data-waslo-connection-identity-note
      >
        {{ t(key('IDENTITY_NOTE')) }}
      </p>
    </div>

    <template v-if="showsBody">
      <span class="w-full h-px mt-2 bg-n-weak" />

      <!-- 4 · WHAT IT SENDS. Only for the calls that send anything. -->
      <div class="flex flex-col gap-6">
        <SettingsHeader
          :heading="t(`${key('BODY')}.HEADING`)"
          :description="t(`${key('BODY')}.DESCRIPTION`)"
        />
        <WasloTextArea
          :model-value="modelValue.bodyText"
          :label="t(`${key('BODY')}.LABEL`)"
          :placeholder="bodyPlaceholder"
          :hint="t(`${key('BODY')}.HINT`, { token: TOKEN_EXAMPLE })"
          :message="errors.body || ''"
          message-type="error"
          min-height="7rem"
          data-waslo-connection-body
          @update:model-value="value => patch({ bodyText: value })"
        />
      </div>
    </template>

    <span class="w-full h-px mt-2 bg-n-weak" />

    <!-- 5 · HOW IT SIGNS IN. -->
    <div class="flex flex-col gap-6">
      <SettingsHeader
        :heading="t(`${key('SIGN_IN')}.HEADING`)"
        :description="t(`${key('SIGN_IN')}.DESCRIPTION`)"
      />
      <div class="flex flex-col gap-6 sm:flex-row sm:items-start">
        <WasloSelect
          class="sm:w-64"
          :model-value="modelValue.auth"
          :label="t(`${key('SIGN_IN')}.LABEL`)"
          :options="authOptions"
          data-waslo-connection-auth
          @update:model-value="value => patch({ auth: value })"
        />
        <WasloInput
          v-if="modelValue.auth === 'header'"
          class="sm:w-64"
          :model-value="modelValue.headerName"
          :label="t(`${key('SIGN_IN')}.HEADER_NAME`)"
          :placeholder="t(`${key('SIGN_IN')}.HEADER_NAME_PLACEHOLDER`)"
          :message="errors.auth || ''"
          :message-type="errors.auth ? 'error' : 'info'"
          data-waslo-connection-header-name
          @update:model-value="value => patch({ headerName: value })"
        />
      </div>
      <WasloInput
        v-if="modelValue.auth !== 'none'"
        type="password"
        :model-value="modelValue.secret"
        :label="t(`${key('SIGN_IN')}.SECRET`)"
        :hint="secretHint"
        :message="errors.secret || ''"
        :message-type="errors.secret ? 'error' : 'info'"
        data-waslo-connection-secret
        @update:model-value="value => patch({ secret: value })"
      />
    </div>

    <span class="w-full h-px mt-2 bg-n-weak" />

    <!-- 6 · WHAT TO KEEP. -->
    <div class="flex flex-col gap-6">
      <SettingsHeader
        :heading="t(`${key('KEEP')}.HEADING`)"
        :description="t(`${key('KEEP')}.DESCRIPTION`)"
      />
      <WasloTextArea
        :model-value="modelValue.keepText"
        :label="t(`${key('KEEP')}.LABEL`)"
        :placeholder="t(`${key('KEEP')}.PLACEHOLDER`)"
        :hint="t(`${key('KEEP')}.HINT`)"
        :message="errors.keepFields || ''"
        message-type="error"
        min-height="4rem"
        data-waslo-connection-keep
        @update:model-value="value => patch({ keepText: value })"
      />
    </div>

    <span class="w-full h-px mt-2 bg-n-weak" />

    <!-- 7 · WHETHER IT IS IN USE. -->
    <div class="flex flex-col gap-6">
      <SettingsHeader
        :heading="t(`${key('STATE')}.HEADING`)"
        :description="t(`${key('STATE')}.DESCRIPTION`)"
      />
      <div
        class="flex items-start justify-between gap-6 p-4 outline outline-1 rounded-xl outline-n-weak bg-n-solid-1"
      >
        <div class="flex flex-col gap-1">
          <h3 class="mb-0 text-sm font-medium text-n-slate-12">
            {{ t(`${key('STATE')}.LABEL`) }}
          </h3>
          <p class="mb-0 text-sm text-n-slate-11">{{ stateSentence }}</p>
          <p
            v-if="testHint"
            class="mb-0 text-sm text-n-amber-11"
            data-waslo-connection-test-hint
          >
            {{ testHint }}
          </p>
          <p
            v-if="errors.enabled"
            class="mb-0 text-sm text-n-ruby-9"
            data-waslo-connection-enabled-error
          >
            {{ errors.enabled }}
          </p>
        </div>
        <Switch
          :model-value="modelValue.enabled"
          :aria-label="t(`${key('STATE')}.LABEL`)"
          class="mt-1 shrink-0"
          :disabled="!modelValue.enabled && !canSwitchOn"
          data-waslo-connection-enabled
          @update:model-value="value => patch({ enabled: value })"
        />
      </div>
    </div>

    <!-- The dry run and the removal are the page's, not the form's: one calls a live
         system and the other navigates away. -->
    <slot name="try" />
    <slot name="danger" />
  </div>
</template>
